class Validation {
  static #instance = null;

  // Common non-product page indicators
  #invalidPathPatterns = [
    /^\/?$/, // Main/home page
    /^\/?(?:index|home|main)\.(?:php|html|htm)\/?$/,
    /^\/?about\/?/,
    /^\/?contact\/?/,
    /^\/?cart\/?/,
    /^\/?checkout\/?/,
    /^\/?login\/?/,
    /^\/?register\/?/,
    /^\/?account\/?/,
    /^\/?profile\/?/,
    /^\/?search\/?/,
    /^\/?category\/?$/,
    /^\/?catalog\/?$/,
    /^\/?blog\/?$/,
    /^\/?news\/?/,
    /^\/?faq\/?/,
    /^\/?help\/?/,
    /^\/?support\/?/,
    /^\/?terms\/?/,
    /^\/?privacy\/?/,
    /^\/?shipping\/?/,
    /^\/?returns\/?/,
    /^\/?sitemap\/?/,
  ];

  // Product URL indicators
  #productIndicators = [
    "/product/",
    "/item/",
    "/goods/",
    "/detail/",
    "/prod/",
    "/p/",
    "/i/",
    "/buy/",
    "-p-",
    "/dp/",
    "/pd/",
    "/id/",
  ];

  // Known shopping domains
  #knownDomains = [
    "th.shein.com",
    "www.lazada.co.th",
    "shopee.co.th",
    "www.nike.com",
    "www.converse.co.th",
    "www.shopat24.com",
    "th.hm.com",
    "www.studio7thailand.com",
    "www.central.co.th",
    "www.homepro.co.th",
    "www.supersports.co.th",
    "www.decathlon.co.th",
    "th.iherb.com",
    "ru.iherb.com",
    "www.ebay.com",
    "www.amazon.com",
    "www.allonline.7eleven.co.th",
  ];

  constructor() {
    if (Validation.#instance) {
      return Validation.#instance;
    }
    Validation.#instance = this;
  }

  // Validate if string is a valid URL
  isValidUrl(url) {
    const urlPattern = new RegExp(
      "^(https?:\\/\\/)?" + // protocol
        "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
        "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
        "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
        "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
        "(\\#[-a-z\\d_]*)?$",
      "i"
    );
    return urlPattern.test(url);
  }

  // Clean URL by removing query parameters and specific appendices
  removeQueryFromUrl(url) {
    try {
      const urlObject = new URL(url);
      urlObject.search = "";
      urlObject.hash = "";
      let cleanUrl = urlObject.toString();

      // Remove Amazon referral tags
      if (cleanUrl.includes("/ref=")) {
        cleanUrl = cleanUrl.split("/ref=")[0];
      }

      return cleanUrl;
    } catch (error) {
      console.error("Invalid URL:", error.message);
      return url;
    }
  }

  // Check if domain is from known shopping sites
  isKnownDomain(domain) {
    return this.#knownDomains.some((knownDomain) =>
      domain.toLowerCase().includes(knownDomain.toLowerCase())
    );
  }

  // Validate if URL looks like a product page
  isLikelyProductUrl(url) {
    try {
      const urlObject = new URL(url);
      const path = urlObject.pathname;

      // Check if it's not a common non-product page
      if (this.#invalidPathPatterns.some((pattern) => pattern.test(path))) {
        return {
          isValid: false,
          message: "Похоже, это не ссылка на товар. Пожалуйста, проверьте URL.",
        };
      }

      // Path should be longer than 3 characters
      if (path.length < 3) {
        return {
          isValid: false,
          message:
            "Слишком короткий URL. Пожалуйста, проверьте ссылку на товар.",
        };
      }

      // Check for product indicators
      const hasProductIndicator = this.#productIndicators.some((indicator) =>
        path.toLowerCase().includes(indicator.toLowerCase())
      );

      // Check for numeric ID in path (at least 4 digits)
      const hasNumericId = /\d{4,}/.test(path);

      if (!hasProductIndicator && !hasNumericId) {
        return {
          isValid: false,
          message:
            "Не удалось определить идентификатор товара в URL. Пожалуйста, проверьте ссылку.",
        };
      }

      return {
        isValid: true,
        message: "URL прошел валидацию",
      };
    } catch (error) {
      return {
        isValid: false,
        message: "Неверный формат URL. Пожалуйста, проверьте ссылку.",
      };
    }
  }

  // Main validation method that combines all checks
  validateProductUrl(url) {
    // First check if it's a valid URL
    if (!this.isValidUrl(url)) {
      return {
        isValid: false,
        message: "Неверный формат URL. Пожалуйста, введите корректную ссылку.",
      };
    }

    // Clean the URL
    const cleanUrl = this.removeQueryFromUrl(url);
    const urlObject = new URL(cleanUrl);
    const domain = urlObject.hostname;

    // Check if it's a known domain
    const isKnown = this.isKnownDomain(domain);

    // If it's a known domain, consider it valid
    if (isKnown) {
      return {
        isValid: true,
        message: "URL прошел валидацию",
        isKnownDomain: true,
      };
    }

    // For unknown domains, perform additional validation
    const productValidation = this.isLikelyProductUrl(cleanUrl);

    return {
      ...productValidation,
      isKnownDomain: false,
    };
  }
}

export default Validation;
